// src/components/ui/ContentCard.tsx
import React from 'react'
import { Link } from 'react-router-dom'
import { LazyImage } from './LazyImage'
import { SocialShare } from './SocialShare'
import { CategoryTag } from './CategoryTag'
import { FaRegCalendarAlt, FaRegClock } from 'react-icons/fa'
import { MdMenuBook } from 'react-icons/md'

interface ContentCardProps {
  title: string
  description: string
  image: string
  date: string
  tags: string[]
  id: string

  internalLink?: string
  externalLink?: string

  readTime?: string
  journal?: string

  categoryLabel?: string
  showSocial?: boolean

  maxTitleChars?: number
  maxDescChars?: number
}

export const ContentCard: React.FC<ContentCardProps> = ({
  title,
  description,
  image,
  date,
  tags,
  internalLink,
  externalLink,
  readTime,
  journal,
  categoryLabel,
  showSocial = false,
  maxTitleChars = 80,
  maxDescChars = 200,
}) => {
  // Simple truncate helper
  const truncate = (str: string, max: number) => {
    if (str.length <= max) return str
    let cut = str.slice(0, max)
    const lastSpace = cut.lastIndexOf(' ')
    if (lastSpace > 0) cut = cut.slice(0, lastSpace)
    return cut + '…'
  }

  const displayTitle = truncate(title.split(/[:\-–—]/)[0], maxTitleChars)
  const displayDesc = truncate(description, maxDescChars)

  const meta1 = readTime
    ? { icon: <FaRegClock />, label: readTime }
    : journal
    ? { icon: <MdMenuBook />, label: journal }
    : null
  const meta2 = { icon: <FaRegCalendarAlt />, label: date }

  const shareUrl = externalLink
    ? externalLink
    : internalLink
    ? window.location.origin + internalLink
    : window.location.href

  const Wrapper: React.FC<React.PropsWithChildren> = ({ children }) =>
    internalLink ? (
      <Link to={internalLink} className="group block">
        {children}
      </Link>
    ) : externalLink ? (
      <a
        href={externalLink}
        target="_blank"
        rel="noopener noreferrer"
        className="group block"
      >
        {children}
      </a>
    ) : (
      <>{children}</>
    )

  return (
    <article className="flex flex-col h-full bg-surface rounded-xl border border-border hover:border-primary transition-colors">
      
      {/* 1. Image */}
      <div className="relative h-48 overflow-hidden">
        <Wrapper>
          <LazyImage
            src={image}
            alt={title}
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
          />
          {categoryLabel && (
            <CategoryTag
              label={categoryLabel}
              className="absolute top-3 left-3"
            />
          )}
        </Wrapper>
      </div>

      {/* 2. Body */}
      <div className="p-6 flex flex-col flex-1">
        
        {/* 2.1 Title (3 lines, fixed 6rem height) */}
        <Wrapper>
          <h2
            className="
              text-2xl font-bold mb-2
              text-text hover:text-primary transition-colors
              line-clamp-3
              min-h-[6rem]
            "
          >
            {displayTitle}
          </h2>
        </Wrapper>

        {/* 2.2 Description (4 lines, fixed 6rem height) */}
        <p
          className="
            text-text mb-4
            line-clamp-4
            min-h-[6rem]
          "
        >
          {displayDesc}
        </p>

        {/* 2.3 Meta line 1 */}
        {meta1 && (
          <div className="flex items-center gap-2 text-text/70 text-sm mb-1">
            {meta1.icon}
            <span>{meta1.label}</span>
          </div>
        )}

        {/* 2.4 Date */}
        <div className="flex items-center gap-2 text-text/70 text-sm mb-4">
          {meta2.icon}
          <span>{meta2.label}</span>
        </div>

        {/* 2.5 Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {tags.map((t) => (
            <CategoryTag key={t} label={t} />
          ))}
        </div>

        {/* 2.6 Social (bottom‑right) */}
        {showSocial && (
          <div className="mt-auto pt-4 border-t border-primary/20 flex justify-end">
            <SocialShare url={shareUrl} title={title} />
          </div>
        )}
      </div>
    </article>
  )
}
